import React, { useState } from 'react';
import PainelDoRateio from './PainelDoRateio';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { RateioDoGrupo, fatiaDaEmpresa, fmtPercentual } from '../utils/rateioDoGrupo';
import { fmtCompetencia } from '../utils/formatters';

/**
 * RATEIO: quanto esta empresa arca do que o grupo compartilha, e por quê.
 *
 * Existia só no modal de EDIÇÃO; quem abria a assinatura só para consultar não
 * via, e é a informação que mais gera dúvida em grupo. Agora o olho (ficha) e a
 * edição usam este mesmo componente. Só aparece quando o grupo tem rateio.
 * "ver divisão" abre o painel com a fatia de cada empresa, critério, base e
 * versões. Valores da API, nunca recalculados.
 */
interface Props {
  rateio: RateioDoGrupo | null;
  clientId: number;
  nome: (id: number) => string;
  fmt: (v: number) => string;
  compacto?: boolean;
}

const FaixaDoRateio: React.FC<Props> = ({ rateio, clientId, nome, fmt, compacto }) => {
  const [aberta, setAberta] = useState(false);
  if (!rateio || rateio.temRateio === false || !clientId) return null;
  const f = fatiaDaEmpresa(rateio, clientId);
  if (!f) return null;
  const alerta = !rateio.somaVigenteFecha ? 'a divisão do grupo não soma 100%' : rateio.divergente ? 'o critério hoje daria outra divisão' : null;
  return (
    // Tom `group` do mapa (§4): superfície, borda, selo e texto do grupo vêm de statusColors.
    <div className={compacto ? `border-b ${TONE_BORDER.group} ${TONE_SURFACE.group}` : `rounded-lg border ${TONE_BORDER.group} ${TONE_SURFACE.group}`}>
      <div className={`flex flex-wrap items-center gap-2 ${compacto ? 'px-5 py-2.5' : 'px-4 py-3'} text-sm`}>
        <span className={`px-1.5 py-0.5 rounded text-xs font-medium uppercase tracking-wide ${TONE_BADGE.group}`}>rateio</span>
        <span className='text-slate-300'>
          Acordado: <b className='text-white'>{fmtPercentual(f.percentual)}</b> do que o grupo compartilha{f.versao ? `, pela versão ${fmtCompetencia(f.versao)}` : ''}
          {f.acordado != null
            ? <>: <b className='text-white'>R$ {fmt(f.acordado)}</b> acordados de R$ {fmt(rateio.pool)}</>
            : <> · cobra hoje <b className='text-white'>R$ {fmt(f.cobrado)}</b> de R$ {fmt(rateio.pool)}</>}
        </span>
        {/* O cobrado só aparece ao lado do acordado quando NÃO honra o acordo: aí é a notícia. */}
        {f.acordado != null && f.deltaAcordadoVsCobrado != null && Math.round(f.deltaAcordadoVsCobrado * 100) !== 0 && (
          <span className={`text-xs ${TONE_TEXT.warning}`} data-testid='faixa-cobrado'>
            cobra hoje R$ {fmt(f.cobrado)} ({f.deltaAcordadoVsCobrado > 0 ? '+' : '−'}R$ {fmt(Math.abs(f.deltaAcordadoVsCobrado))})
          </span>
        )}
        {/* A base de HOJE, em afirmação separada: cada número ao lado da coisa de que ele é verdade. */}
        {f.hoje.base && f.hoje.percentual !== f.percentual && (
          <span className='text-xs text-slate-400' data-testid='faixa-hoje'>
            hoje o critério daria {fmtPercentual(f.hoje.percentual)}, com {f.hoje.base}
          </span>
        )}
        {alerta && <span className={`text-xs ${!rateio.somaVigenteFecha ? TONE_TEXT.danger : TONE_TEXT.warning}`}>⚠ {alerta}</span>}
        <button type='button' onClick={() => setAberta(!aberta)} className={`ml-auto text-xs ${TONE_TEXT.group} hover:text-white`}>
          {aberta ? 'esconder divisão' : 'ver divisão →'}
        </button>
      </div>
      {aberta && (
        <div className={`border-t ${TONE_BORDER.group} ${compacto ? 'px-5' : 'px-4'} py-3`}>
          <PainelDoRateio clientId={clientId} nome={nome} destacar={clientId} />
        </div>
      )}
    </div>
  );
};

export default FaixaDoRateio;
